import { isListingDurationAvailable } from '@popety-io/utils'

type ListingContactClassification = 'individual' | 'professional' | 'unknown'

interface ListingDurationData {
  active?: boolean
  active_listing_timestamp?: string
  ad_status?: boolean
  listing_duration?: number | null
  listing_timestamp?: string
}

interface ListingDurationTranslations {
  day: string
  days: string
}

interface ListingDurationLabelTranslations {
  onlineFor: string
  onlineDuring?: string
}

const individualVendorTypes = new Set(['private', 'individual'])
const professionalVendorTypes = new Set([
  'pro',
  'professional',
  'agency',
  'company',
])

const getListingContactClassification = (
  vendorType?: string | null,
  contactType?: string | null
): ListingContactClassification => {
  if (vendorType === 'unknown' || vendorType === '') return 'unknown'
  if (vendorType && individualVendorTypes.has(vendorType)) return 'individual'
  if (vendorType && professionalVendorTypes.has(vendorType)) {
    return 'professional'
  }
  if (contactType && individualVendorTypes.has(contactType)) return 'individual'
  if (contactType && professionalVendorTypes.has(contactType)) {
    return 'professional'
  }
  return 'unknown'
}

export const getListingContactType = (
  vendorType?: string | null,
  contactType?: string | null
): 'individual' | 'professional' =>
  getListingContactClassification(vendorType, contactType) === 'individual'
    ? 'individual'
    : 'professional'

interface ListingContactRoleTranslations {
  privateVendor: string
  professionalVendor: string
}

export const getListingContactRole = (
  vendorType: string | null | undefined,
  contactType: string | null | undefined,
  translations: ListingContactRoleTranslations
): string | undefined => {
  const classification = getListingContactClassification(
    vendorType,
    contactType
  )

  if (classification === 'individual') return translations.privateVendor
  if (classification === 'professional') {
    return translations.professionalVendor
  }
  return undefined
}

const formatListingDuration = (
  duration: number | null | undefined,
  translations: ListingDurationTranslations
): string | null => {
  if (!isListingDurationAvailable(duration)) return null
  return `${duration} ${duration === 1 ? translations.day : translations.days}`
}

const isListingActive = (listing: ListingDurationData): boolean =>
  listing.ad_status ?? listing.active ?? true

/**
 * Active listings are measured from their latest active timestamp until now.
 * Expired listings display the duration persisted when the source deactivated
 * the ad.
 */
export const getListingOnlineDuration = (
  listing: ListingDurationData | null | undefined,
  translations: ListingDurationTranslations,
  now = new Date()
): string | null => {
  if (!listing) return null

  if (isListingActive(listing)) {
    const activeAt = listing.active_listing_timestamp
      ? new Date(listing.active_listing_timestamp).getTime()
      : Number.NaN

    if (!Number.isFinite(activeAt)) return null

    const duration = Math.ceil(Math.abs(now.getTime() - activeAt) / 86_400_000)
    return formatListingDuration(duration, translations)
  }

  return formatListingDuration(listing.listing_duration, translations)
}

export const getListingOnlineDurationLabel = (
  listing: ListingDurationData | null | undefined,
  translations: ListingDurationLabelTranslations
): string =>
  listing && !isListingActive(listing)
    ? translations.onlineDuring || translations.onlineFor
    : translations.onlineFor
